import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate } from "react-router-dom";
import { landingAssetsApi } from "../../api/landingAssets";
import { settingsApi } from "../../api/settings";
import PendingAssetField from "../../components/dashboard/landingEditor/PendingAssetField";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import ProjectDeploymentCard from "../../components/dashboard/ProjectDeploymentCard";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { assetValueFromResponse } from "../../utils/landingAssets";
import { resolveProjectNavigation } from "../../utils/dashboardNavigation";
import { hasProjectRole, normalizeProjectAccess } from "../../utils/projectAccess";
import { isSuperAdmin } from "../../utils/roles";
import {
  PROJECT_BUSINESS_PROFILES,
  capabilitiesForBusinessGoals,
} from "../../utils/projectCapabilities";
import {
  activeModuleLabels,
  BILLING_PLAN_LABELS,
  modulesForPlan,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  yellow: "#FFC857",
  green: "#26A269",
  blue: "#2F80ED",
};
const emptyBranding = { logo: null, compactLogo: null, favicon: null };

function emptyForm(email = "", plan = "catalogo") {
  return {
    name: "",
    slug: "",
    ownerEmail: email,
    plan,
    businessProfile: "",
    businessGoals: [],
    domains: "",
  };
}

function makeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadInitialBranding(projectId, branding, businessName) {
  const uploads = [
    {
      file: branding.logo,
      field: "logoUrl",
      sectionKey: "branding",
      assetKey: "logo",
    },
    {
      file: branding.compactLogo,
      field: "compactLogoUrl",
      sectionKey: "branding",
      assetKey: "compact-logo",
    },
    {
      file: branding.favicon,
      field: "faviconUrl",
      sectionKey: "branding",
      assetKey: "favicon",
    },
  ].filter((item) => item.file);

  if (!uploads.length) return;

  const uploadedPaths = [];
  const theme = {};

  try {
    for (const item of uploads) {
      const response = await landingAssetsApi.upload(projectId, item.file, {
        pageKey: "home",
        sectionKey: item.sectionKey,
        assetKey: item.assetKey,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      uploadedPaths.push(value);
      theme[item.field] = value;
    }

    await settingsApi.update(projectId, {
      business: { name: businessName },
      seo: { title: businessName },
      theme,
    });
  } catch (error) {
    await Promise.all(
      uploadedPaths.map((path) => landingAssetsApi.remove(projectId, path).catch(() => null))
    );
    throw error;
  }
}

const FIRST_PROJECT_BINARY_STEPS = Object.freeze([
  {
    id: "products",
    answerKey: "products",
    title: "¿Vendés productos?",
    help: "Esto prioriza catálogo, pedidos y herramientas de venta.",
    detail: "Elegí Sí si cargás artículos, mercadería o cualquier producto con precio. Podés cambiar esta decisión después.",
  },
  {
    id: "services",
    answerKey: "services",
    title: "¿Ofrecés servicios?",
    help: "Esto prioriza servicios, agenda y seguimiento de la atención.",
    detail: "Incluye turnos, trabajos, clases, consultas u otros servicios que tu negocio presta a clientes.",
  },
  {
    id: "customers",
    answerKey: "customers",
    title: "¿Querés gestionar clientes?",
    help: "Activa el recorrido inicial hacia CRM y seguimiento de clientes.",
    detail: "Sirve si necesitás guardar clientes, datos de contacto, historial o acciones comerciales desde WebProfit.",
  },
  {
    id: "memberships",
    answerKey: "memberships",
    title: "¿Usás membresías o planes?",
    help: "Esto prepara el circuito para asignar planes a clientes.",
    detail: "Es útil para gimnasios, academias, clubes o negocios que cobran abonos y renovaciones periódicas.",
  },
  {
    id: "menu",
    answerKey: "menu",
    title: "¿Necesitás una carta?",
    help: "Esto habilita la carta gastronómica sobre tus mismos ítems.",
    detail: "Elegí Sí si querés publicar platos, bebidas o productos organizados como una carta para tus clientes.",
  },
  {
    id: "tables",
    answerKey: "tables",
    title: "¿Trabajás con mesas?",
    help: "Esto prepara mesas y reservas de mesa para la operación gastronómica.",
    detail: "Elegí Sí si necesitás organizar un salón, asignar mesas o recibir reservas vinculadas a mesas.",
  },
  {
    id: "management",
    answerKey: "management",
    title: "¿Querés controlar la gestión?",
    help: "Esto prioriza sistema interno, métricas y control operativo.",
    detail: "Elegí Sí si además de vender o atender querés seguir la operación y revisar indicadores del negocio.",
  },
]);

const REAL_ESTATE_PROJECT_BINARY_STEPS = Object.freeze([
  {
    id: "properties",
    answerKey: "properties",
    title: "¿Querés gestionar propiedades?",
    help: "Prepara el contrato para organizar el inventario inmobiliario.",
    detail: "Incluye propiedades disponibles, datos comerciales y futura operación inmobiliaria sin mezclarla con el catálogo común.",
  },
  {
    id: "leases",
    answerKey: "leases",
    title: "¿Gestionás contratos o alquileres?",
    help: "Prepara el seguimiento de contratos como capacidad independiente.",
    detail: "Elegí Sí si necesitás administrar alquileres y vincularlos con propiedades y clientes.",
  },
  {
    id: "leaseCollections",
    answerKey: "leaseCollections",
    title: "¿Querés gestionar cobranzas de alquileres?",
    help: "Prepara una capacidad de cobranzas separada del resto de la gestión.",
    detail: "Permite asignar permisos específicos de cobranza sin habilitar páginas inmobiliarias todavía.",
  },
]);

const REAL_ESTATE_GUIDED_ANSWER_KEYS = Object.freeze(
  REAL_ESTATE_PROJECT_BINARY_STEPS.map(({ answerKey }) => answerKey),
);

function guidedAnswersForProfile(businessProfile, answers = {}) {
  if (businessProfile === "real_estate") return answers;

  return {
    ...answers,
    ...Object.fromEntries(REAL_ESTATE_GUIDED_ANSWER_KEYS.map((answerKey) => [answerKey, null])),
  };
}

function guidedBusinessGoals(businessProfile, answers = {}) {
  const applicableAnswers = guidedAnswersForProfile(businessProfile, answers);
  const goals = [];
  if (applicableAnswers.products === true) goals.push("sellProducts");
  if (applicableAnswers.services === true) goals.push("offerServices");
  if (applicableAnswers.customers === true) goals.push("manageCustomers");
  if (applicableAnswers.memberships === true) goals.push("manageMemberships");
  if (applicableAnswers.menu === true) goals.push("gastronomicMenu");
  if (applicableAnswers.tables === true) goals.push("takeReservations");
  if (applicableAnswers.management === true) goals.push("controlBusiness");
  if (applicableAnswers.properties === true) goals.push("manageProperties");
  if (applicableAnswers.leases === true) goals.push("manageLeases");
  if (applicableAnswers.leaseCollections === true) goals.push("manageLeaseCollections");
  return goals;
}

function guidedCapabilities(businessProfile, answers = {}) {
  const applicableAnswers = guidedAnswersForProfile(businessProfile, answers);
  const goals = guidedBusinessGoals(businessProfile, applicableAnswers);
  const capabilities = capabilitiesForBusinessGoals(businessProfile, goals);

  return {
    ...capabilities,
    memberships: applicableAnswers.memberships === true,
    gastronomicMenu: applicableAnswers.menu === true,
    tables: applicableAnswers.tables === true,
    tableReservations: applicableAnswers.tables === true,
    properties: applicableAnswers.properties === true,
    leases: applicableAnswers.leases === true,
    leaseCollections: applicableAnswers.leaseCollections === true,
  };
}

function ProjectActionsMenu({
  project,
  superAdmin,
  showPlan,
  showLanding,
  showProducts,
  showOrders,
  onNavigate,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const slug = project?.slug;
  const actions = [
    superAdmin
      ? { key: "manage", label: "Administrar", path: `/dashboard/proyectos/${slug}/configuracion/general` }
      : null,
    showPlan
      ? { key: "plan", label: "Plan y suscripción", path: `/dashboard/proyectos/${slug}/configuracion/suscripcion` }
      : null,
    showLanding
      ? { key: "landing", label: "Landing", path: `/dashboard/proyectos/${slug}/pagina` }
      : null,
    showProducts
      ? { key: "products", label: "Productos", path: `/dashboard/proyectos/${slug}/productos` }
      : null,
    showOrders
      ? { key: "orders", label: "Pedidos", path: `/dashboard/proyectos/${slug}/pedidos` }
      : null,
  ].filter(Boolean);

  if (!actions.length) return null;

  const go = (path) => {
    setAnchorEl(null);
    onNavigate(path);
  };

  return (
    <>
      <Button
        type="button"
        fullWidth
        variant="outlined"
        endIcon={<MoreHorizRoundedIcon />}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minHeight: 42,
          borderRadius: 2.2,
          textTransform: "none",
          fontWeight: 900,
          color: BRAND.dark,
          borderColor: "rgba(25,36,47,.14)",
        }}
      >
        Más acciones
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: .6,
            width: 260,
            maxWidth: "calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right))",
            maxHeight: "calc(100dvh - 96px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
            borderRadius: 2.5,
            border: "1px solid rgba(25,36,47,.08)",
            boxShadow: "0 18px 46px rgba(25,36,47,.16)",
          },
        }}
        MenuListProps={{ "aria-label": `Acciones de ${project?.name || "proyecto"}` }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => go(action.path)}
            sx={{ minHeight: 44, fontSize: ".86rem", fontWeight: 850 }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function FirstProjectOnboarding({
  form,
  creating,
  createStatus,
  error,
  onChange,
  onSubmit,
}) {
  const [stepIndex, setStepIndex] = React.useState(0);
  const [profileAnswered, setProfileAnswered] = React.useState(Boolean(form.businessProfile));
  const [answers, setAnswers] = React.useState(() => Object.fromEntries(
    [...FIRST_PROJECT_BINARY_STEPS, ...REAL_ESTATE_PROJECT_BINARY_STEPS].map(({ answerKey }) => [answerKey, null]),
  ));

  const steps = React.useMemo(() => ([
    {
      id: "name",
      title: "¿Cómo se llama tu negocio?",
      help: "Es el único dato obligatorio para crear el proyecto.",
      detail: "La dirección inicial de tu web se genera automáticamente con este nombre y después podés cambiarla desde Configuración.",
    },
    {
      id: "profile",
      title: "¿Cuál es tu rubro principal?",
      help: "Lo usamos para ordenar sugerencias; no limita herramientas.",
      detail: "Si tu negocio mezcla actividades o todavía no querés definirlo, elegí “Otro / decidir después”.",
    },
    ...FIRST_PROJECT_BINARY_STEPS,
    ...(form.businessProfile === "real_estate" ? REAL_ESTATE_PROJECT_BINARY_STEPS : []),
  ]), [form.businessProfile]);

  const currentStep = steps[stepIndex];
  const lastStep = stepIndex === steps.length - 1;
  const generatedSlug = makeSlug(form.slug || form.name);
  const nameReady = Boolean(form.name.trim() && generatedSlug);
  const binaryAnswer = currentStep?.answerKey ? answers[currentStep.answerKey] : null;
  const canContinue = currentStep?.id === "name"
    ? nameReady
    : currentStep?.id === "profile"
      ? profileAnswered
      : binaryAnswer !== null;
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);

  const selectProfile = (value) => {
    if (form.businessProfile === "real_estate" && value !== "real_estate") {
      setAnswers((previous) => guidedAnswersForProfile(value, previous));
    }
    setProfileAnswered(true);
    onChange({ target: { name: "businessProfile", value } });
  };

  const selectAnswer = (answerKey, value) => {
    setAnswers((previous) => ({ ...previous, [answerKey]: value }));
  };

  const goBack = () => {
    if (creating) return;
    setStepIndex((current) => Math.max(0, current - 1));
  };

  const handleWizardSubmit = (event) => {
    event.preventDefault();
    if (!canContinue || creating) return;

    if (!lastStep) {
      setStepIndex((current) => Math.min(steps.length - 1, current + 1));
      return;
    }

    onSubmit(event, {
      businessGoals: guidedBusinessGoals(form.businessProfile, answers),
      capabilities: guidedCapabilities(form.businessProfile, answers),
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleWizardSubmit}
      sx={{
        width: "100%",
        maxWidth: 760,
        mx: "auto",
        pt: { xs: .5, sm: 2, md: 4 },
        pb: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: 2.75, sm: 4 },
          border: "1px solid rgba(25,36,47,.09)",
          bgcolor: "#fff",
          boxShadow: { xs: "0 10px 30px rgba(25,36,47,.08)", sm: "0 22px 60px rgba(25,36,47,.09)" },
        }}
      >
        <Box sx={{ px: { xs: 1.6, sm: 3, md: 4 }, pt: { xs: 1.5, sm: 2.5 }, pb: 1.4 }}>
          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 2.3,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  bgcolor: "rgba(243,90,58,.1)",
                  color: BRAND.orange,
                }}
              >
                <StorefrontRoundedIcon sx={{ fontSize: 21 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: BRAND.orange, fontSize: ".68rem", fontWeight: 1000, letterSpacing: .55 }}>
                  CREAR NEGOCIO
                </Typography>
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".8rem", sm: ".88rem" }, fontWeight: 850 }}>
                  Paso {stepIndex + 1} de {steps.length}
                </Typography>
              </Box>
            </Stack>
            <Typography sx={{ color: "rgba(25,36,47,.48)", fontSize: ".74rem", fontWeight: 850 }}>
              {progress}%
            </Typography>
          </Stack>

          <Box sx={{ mt: 1.2, height: 5, borderRadius: 999, bgcolor: "rgba(25,36,47,.07)", overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${progress}%`,
                borderRadius: 999,
                bgcolor: BRAND.orange,
                transition: "width .2s ease",
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            px: { xs: 1.6, sm: 3, md: 4 },
            py: { xs: 2, sm: 3.2, md: 4 },
            minHeight: { xs: 310, sm: 340 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {error ? (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2.2 }}>
              {error}
            </Alert>
          ) : null}

          <Typography
            component="h1"
            sx={{
              color: BRAND.dark,
              fontSize: { xs: "1.45rem", sm: "1.9rem" },
              lineHeight: 1.15,
              fontWeight: 1000,
              letterSpacing: { xs: -.35, sm: -.7 },
            }}
          >
            {currentStep.title}
          </Typography>
          <Typography sx={{ mt: .65, color: "rgba(25,36,47,.62)", fontSize: { xs: ".84rem", sm: ".94rem" }, lineHeight: 1.45 }}>
            {currentStep.help}
          </Typography>

          <Box sx={{ mt: { xs: 2, sm: 2.5 } }}>
            {currentStep.id === "name" ? (
              <TextField
                autoFocus
                label="Nombre del negocio"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                fullWidth
                placeholder="Ej: Repuestos RR"
                autoComplete="organization"
                inputProps={{ enterKeyHint: "next" }}
                InputProps={{ sx: { borderRadius: 2.5, minHeight: 52 } }}
              />
            ) : null}

            {currentStep.id === "profile" ? (
              <Grid container spacing={1}>
                {PROJECT_BUSINESS_PROFILES.map((profile) => {
                  const active = profileAnswered && form.businessProfile === profile.key;
                  return (
                    <Grid item xs={12} sm={6} key={profile.key}>
                      <Button
                        type="button"
                        fullWidth
                        variant={active ? "contained" : "outlined"}
                        onClick={() => selectProfile(profile.key)}
                        sx={{
                          minHeight: 52,
                          justifyContent: "flex-start",
                          px: 1.5,
                          borderRadius: 2.4,
                          textTransform: "none",
                          fontWeight: 900,
                          bgcolor: active ? BRAND.purple : "#fff",
                          borderColor: active ? BRAND.purple : "rgba(25,36,47,.16)",
                          color: active ? "#fff" : BRAND.dark,
                          boxShadow: "none",
                          "&:hover": { bgcolor: active ? "#5a49d6" : "rgba(105,87,232,.04)", boxShadow: "none" },
                        }}
                      >
                        {profile.label}
                      </Button>
                    </Grid>
                  );
                })}
                <Grid item xs={12} sm={6}>
                  <Button
                    type="button"
                    fullWidth
                    variant={profileAnswered && !form.businessProfile ? "contained" : "outlined"}
                    onClick={() => selectProfile("")}
                    sx={{
                      minHeight: 52,
                      justifyContent: "flex-start",
                      px: 1.5,
                      borderRadius: 2.4,
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: profileAnswered && !form.businessProfile ? BRAND.purple : "#fff",
                      borderColor: profileAnswered && !form.businessProfile ? BRAND.purple : "rgba(25,36,47,.16)",
                      color: profileAnswered && !form.businessProfile ? "#fff" : BRAND.dark,
                      boxShadow: "none",
                    }}
                  >
                    Otro / decidir después
                  </Button>
                </Grid>
              </Grid>
            ) : null}

            {currentStep.answerKey ? (
              <Grid container spacing={1.1}>
                {[
                  { value: true, label: "Sí" },
                  { value: false, label: "Ahora no" },
                ].map((option) => {
                  const active = answers[currentStep.answerKey] === option.value;
                  return (
                    <Grid item xs={6} key={option.label}>
                      <Button
                        type="button"
                        fullWidth
                        variant={active ? "contained" : "outlined"}
                        onClick={() => selectAnswer(currentStep.answerKey, option.value)}
                        sx={{
                          minHeight: { xs: 56, sm: 60 },
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontSize: { xs: ".95rem", sm: "1rem" },
                          fontWeight: 950,
                          bgcolor: active ? BRAND.purple : "#fff",
                          borderColor: active ? BRAND.purple : "rgba(25,36,47,.16)",
                          color: active ? "#fff" : BRAND.dark,
                          boxShadow: "none",
                          "&:hover": { bgcolor: active ? "#5a49d6" : "rgba(105,87,232,.04)", boxShadow: "none" },
                        }}
                      >
                        {option.label}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}
          </Box>

          <Box component="details" sx={{ mt: "auto", pt: 2.2, color: "rgba(25,36,47,.6)" }}>
            <Box
              component="summary"
              sx={{
                cursor: "pointer",
                width: "fit-content",
                fontSize: ".78rem",
                fontWeight: 850,
                "&::marker": { color: BRAND.purple },
              }}
            >
              Más ayuda
            </Box>
            <Typography sx={{ mt: .7, fontSize: ".78rem", lineHeight: 1.45, maxWidth: 620 }}>
              {currentStep.detail}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            px: { xs: 1.25, sm: 2.2, md: 3 },
            py: { xs: 1.1, sm: 1.5 },
            bgcolor: "rgba(25,36,47,.018)",
          }}
        >
          {createStatus ? (
            <Alert severity="info" sx={{ mb: 1, borderRadius: 2.1, py: .2 }}>
              {createStatus}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              onClick={goBack}
              disabled={creating || stepIndex === 0}
              sx={{
                minWidth: { xs: 92, sm: 112 },
                minHeight: 46,
                borderRadius: 2.3,
                textTransform: "none",
                fontWeight: 900,
                color: BRAND.dark,
              }}
            >
              Volver
            </Button>
            <Button
              type="submit"
              fullWidth
              disabled={creating || !canContinue}
              variant="contained"
              endIcon={!creating ? <ArrowForwardRoundedIcon /> : null}
              sx={{
                minHeight: 46,
                borderRadius: 2.3,
                bgcolor: BRAND.orange,
                textTransform: "none",
                fontWeight: 1000,
                boxShadow: "none",
                "&:hover": { bgcolor: "#dd472b", boxShadow: "none" },
              }}
            >
              {creating ? "Creando tu proyecto..." : lastStep ? "Crear mi proyecto" : "Continuar"}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const {
    projects,
    activeProject,
    projectCreation,
    setActiveProject,
    createProject,
    loadProjects,
    loading,
    error,
  } = useProjects();
  const [form, setForm] = React.useState(() => emptyForm(
    user?.email || "",
    "plus",
  ));
  const [branding, setBranding] = React.useState(emptyBranding);
  const [localError, setLocalError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createStatus, setCreateStatus] = React.useState("");
  const compactProjectCards = useMediaQuery("(max-width:899.95px)");

  const ownedCount = Number(projectCreation?.ownedCount ?? (superAdmin ? 0 : projects.length));
  const canCreateProject = superAdmin || Boolean(
    projectCreation?.canCreate ?? (!superAdmin && projects.length === 0),
  );
  const showCreatePanel = superAdmin || canCreateProject;
  const creationReason = String(projectCreation?.reason || "");
  const multiProjectSource = String(projectCreation?.multiProjectSource || "");
  const subscriptionTarget = projectCreation?.subscriptionProject || activeProject || projects[0] || null;
  const firstProjectMode = !superAdmin && projects.length === 0 && canCreateProject;

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setForm((previous) => ({
      ...previous,
      ownerEmail: previous.ownerEmail || user?.email || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "name") next.slug = makeSlug(value);
      return next;
    });
  };

  const handleBrandingChange = (key, file) => {
    setBranding((previous) => ({ ...previous, [key]: file }));
  };

  const handleSubmit = async (event, guidedSetup = null) => {
    event.preventDefault();
    let project = null;

    try {
      setCreating(true);
      setCreateStatus(firstProjectMode ? "Creando tu proyecto..." : "Creando el proyecto y su espacio privado...");
      setLocalError("");

      const createPayload = {
        name: form.name,
        slug: makeSlug(form.slug || form.name),
        ownerEmail: form.ownerEmail,
        plan: form.plan,
        businessProfile: form.businessProfile,
        domains: form.domains,
        modules: modulesForPlan(form.plan),
      };

      if (firstProjectMode) {
        const businessGoals = Array.isArray(guidedSetup?.businessGoals)
          ? guidedSetup.businessGoals
          : form.businessGoals;
        createPayload.businessGoals = businessGoals;
        createPayload.capabilities = guidedSetup?.capabilities || capabilitiesForBusinessGoals(
          form.businessProfile,
          businessGoals,
        );
      }

      project = await createProject(createPayload);

      setCreateStatus(firstProjectMode ? "Proyecto creado. Estamos preparando tu espacio..." : "Proyecto creado. Render está preparando la página pública...");

      const hasBranding = Boolean(branding.logo || branding.compactLogo || branding.favicon);
      if (hasBranding && project?.slug) {
        setCreateStatus("Subiendo tus logos y recursos visuales...");
        await uploadInitialBranding(project.slug, branding, form.name);
      }

      setForm(emptyForm(user?.email || "", "plus"));
      setBranding(emptyBranding);

      if (project?.slug) {
        navigate(firstProjectMode ? "/dashboard" : `/dashboard/proyectos/${project.slug}/pagina`);
      }
    } catch (err) {
      if (project?.slug) {
        setLocalError(
          `El proyecto ${project.name || project.slug} fue creado, pero no se pudo completar la identidad visual: ${err.message || "error desconocido"}. Podés reintentar desde Landing > Ajustes > Recursos visuales.`
        );
        await loadProjects();
      } else {
        setLocalError(err.message || "No se pudo crear el proyecto");
      }
    } finally {
      setCreating(false);
      setCreateStatus("");
    }
  };

  if (firstProjectMode) {
    return (
      <FirstProjectOnboarding
        form={form}
        creating={creating}
        createStatus={createStatus}
        error={localError || error}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>
          Proyectos
        </Typography>
        <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
          {superAdmin
            ? "Creá clientes y administrá sus proyectos."
            : "Elegí un proyecto o agregá uno nuevo cuando tu cuenta lo permita."}
        </Typography>
      </Box>

      {(error || localError) && <Alert severity="error" onClose={() => setLocalError("")}>{localError || error}</Alert>}

      {!superAdmin && !canCreateProject && creationReason === "subscription_required" ? (
        <Paper elevation={0} sx={{ p: { xs: 1.6, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(243,90,58,.22)", bgcolor: "rgba(243,90,58,.045)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.12)", color: BRAND.orange, flexShrink: 0 }}>
                <WorkspacePremiumRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Tu primer proyecto ya está creado
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.66)", maxWidth: 720 }}>
                  Para agregar un segundo proyecto necesitás activar una suscripción en tu proyecto actual. La suscripción habilita un proyecto adicional sin modificar el que ya tenés.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              disabled={!subscriptionTarget?.slug}
              onClick={() => subscriptionTarget?.slug && navigate(`/dashboard/proyectos/${subscriptionTarget.slug}/configuracion/suscripcion`)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minHeight: 42,
                bgcolor: BRAND.orange,
                borderRadius: { xs: 2.2, sm: 999 },
                textTransform: "none",
                fontWeight: 900,
                px: 2.5,
                flexShrink: 0,
              }}
            >
              Activar suscripción
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {!superAdmin && !canCreateProject && creationReason === "limit_reached" ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu cuenta ya utiliza los {projectCreation?.maxProjects || 2} proyectos disponibles: el primero incluido y un proyecto adicional habilitado por tu prueba de 30 días o por una suscripción activa.
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        {showCreatePanel && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: { xs: 1.6, sm: 2.25, md: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: { xs: 1.5, md: 2.5 } }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <AddRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>
                    {superAdmin ? "Nuevo cliente" : "Agregar otro proyecto"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                    {superAdmin
                      ? "Completá lo esencial; lo técnico puede quedar para después."
                      : multiProjectSource === "trial"
                        ? "Tu prueba completa de 30 días habilita un proyecto adicional para esta cuenta."
                        : "Tu suscripción habilitó un proyecto adicional para esta cuenta."}
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={{ xs: 1.4, sm: 2 }}>
                  <TextField label="Nombre del negocio" name="name" value={form.name} onChange={handleChange} required fullWidth />
                  <TextField
                    select
                    label="Tipo de negocio (opcional)"
                    name="businessProfile"
                    value={form.businessProfile}
                    onChange={handleChange}
                    helperText="Sólo ordena las sugerencias iniciales."
                    fullWidth
                  >
                    <MenuItem value="">Configurar después</MenuItem>
                    {PROJECT_BUSINESS_PROFILES.map((profile) => (
                      <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>
                    ))}
                  </TextField>
                  {superAdmin ? (
                    <TextField label="Email owner" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} required fullWidth />
                  ) : null}
                  <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 2.5, bgcolor: "rgba(38,162,105,.045)", borderColor: "rgba(38,162,105,.22)" }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: BRAND.green, fontWeight: 1000 }}>
                          PRUEBA INICIAL AUTOMÁTICA
                        </Typography>
                        <Typography sx={{ mt: 0.2, fontWeight: 950, color: BRAND.dark }}>
                          Sistema completo durante 30 días
                        </Typography>
                      </Box>
                      <ContextualHelp
                        compact
                        title="Qué incluye la prueba"
                        text="El proyecto empieza con todas las herramientas principales habilitadas."
                        bullets={[
                          "Landing, catálogo, pedidos y cobros.",
                          "Sistema interno, estadísticas y Web Push.",
                          "Al finalizar, el cliente elige qué servicios conservar.",
                        ]}
                      />
                    </Stack>
                  </Paper>

                  <Box
                    component="details"
                    {...(!compactProjectCards ? { open: true } : {})}
                    sx={{
                      border: "1px solid rgba(25,36,47,.08)",
                      borderRadius: 2.6,
                      bgcolor: "rgba(25,36,47,.015)",
                      "&[open] summary": { borderBottom: "1px solid rgba(25,36,47,.07)" },
                    }}
                  >
                    <Box
                      component="summary"
                      sx={{
                        px: 1.35,
                        py: 1.15,
                        cursor: "pointer",
                        color: BRAND.dark,
                        fontWeight: 900,
                        fontSize: ".86rem",
                        "&::marker": { color: BRAND.orange },
                      }}
                    >
                      Dirección, dominio e identidad
                    </Box>
                    <Stack spacing={1.4} sx={{ p: 1.35 }}>
                      <TextField
                        label="Dirección inicial"
                        name="slug"
                        value={form.slug}
                        onChange={handleChange}
                        required
                        fullWidth
                        helperText="Se genera desde el nombre; cambiala sólo si necesitás otra."
                      />
                      <TextField
                        label="Dominio público"
                        name="domains"
                        value={form.domains}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        minRows={2}
                        helperText="Opcional. Uno por línea o separados por coma."
                      />

                      <Divider />
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Identidad inicial</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)", mt: 0.2 }}>
                          Opcional; también se puede completar después.
                        </Typography>
                      </Box>
                      <PendingAssetField
                        label="Logo principal"
                        file={branding.logo}
                        disabled={creating}
                        helperText="SVG, PNG, WebP o JPG."
                        onChange={(file) => handleBrandingChange("logo", file)}
                      />
                      <PendingAssetField
                        label="Logo compacto"
                        file={branding.compactLogo}
                        disabled={creating}
                        helperText="Versión reducida para móvil."
                        onChange={(file) => handleBrandingChange("compactLogo", file)}
                      />
                      <PendingAssetField
                        label="Favicon"
                        file={branding.favicon}
                        disabled={creating}
                        accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                        helperText="Preferentemente cuadrado."
                        onChange={(file) => handleBrandingChange("favicon", file)}
                      />
                    </Stack>
                  </Box>

                  {createStatus ? <Alert severity="info">{createStatus}</Alert> : null}
                  <Button
                    type="submit"
                    disabled={creating}
                    variant="contained"
                    size="large"
                    sx={{
                      minHeight: 44,
                      bgcolor: BRAND.orange,
                      borderRadius: { xs: 2.2, sm: 999 },
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    {creating
                      ? "Preparando proyecto..."
                      : superAdmin
                        ? "Crear proyecto"
                        : ownedCount === 0
                          ? "Crear mi primer proyecto"
                          : "Agregar proyecto"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showCreatePanel ? 7 : 12}>
          <Stack spacing={2}>
            {loading && <Alert severity="info">Cargando proyectos...</Alert>}
            {projects.map((projectItem) => {
              const isActive = activeProject?.slug === projectItem.slug;
              const modules = normalizeProjectModules(projectItem.modules);
              const moduleLabels = activeModuleLabels(projectItem);
              const memberAccess = normalizeProjectAccess(projectItem.memberAccess);
              const projectNavigation = resolveProjectNavigation({
                project: projectItem,
                memberAccess,
                superAdmin,
              });
              const landingAccess = projectNavigation.toolsByKey.landing;
              const productsAccess = projectNavigation.toolsByKey.products;
              const ordersAccess = projectNavigation.toolsByKey.orders;
              const planAccess = projectNavigation.toolsByKey.settings?.secondaryTools?.find(
                (tool) => tool.key === "settings-suscripcion",
              );
              const projectUsable = superAdmin || !memberAccess.resolved || memberAccess.status === "active";
              const canRetryDeployment = superAdmin || hasProjectRole(memberAccess, ["owner", "admin"]);
              const planLabel = BILLING_PLAN_LABELS[
                normalizeProjectPlanCode(projectItem.billing?.planCode || projectItem.plan)
              ];
              return (
                <Card
                  key={projectItem.id || projectItem.slug}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    borderRadius: 4,
                    border: `1px solid ${isActive ? BRAND.orange : "rgba(25,36,47,0.08)"}`,
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.5, md: 2 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                          <Typography
                            variant="h5"
                            sx={{ minWidth: 0, maxWidth: "100%", fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}
                          >
                            {projectItem.name}
                          </Typography>
                          {isActive && <Chip label="Activo" size="small" sx={{ bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 900 }} />}
                        </Stack>

                        {compactProjectCards ? (
                          <Stack direction="row" spacing={.65} useFlexGap flexWrap="wrap" sx={{ mt: .8 }}>
                            <Chip size="small" label={planLabel || "Plan"} variant="outlined" sx={{ fontWeight: 850 }} />
                            <Chip
                              size="small"
                              label={projectItem.billing?.status || "trial"}
                              color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"}
                              sx={{ fontWeight: 850 }}
                            />
                          </Stack>
                        ) : (
                          <>
                            <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.75, overflowWrap: "anywhere" }}>
                              /{projectItem.slug} · DB: {projectItem.dbName} · Plan: {planLabel}
                            </Typography>
                            {projectItem.domains?.length ? (
                              <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", mt: 0.45, overflowWrap: "anywhere" }}>
                                Dominios asociados: {projectItem.domains.join(", ")}
                              </Typography>
                            ) : null}
                          </>
                        )}

                        <Box sx={{ mt: { xs: 1, md: 1.5 }, maxWidth: 760 }}>
                          {projectUsable ? (
                            <ProjectDeploymentCard project={projectItem} compact canRetry={canRetryDeployment} />
                          ) : (
                            <Alert severity="warning">Tu membresía en este proyecto está suspendida.</Alert>
                          )}
                        </Box>

                        {compactProjectCards ? (
                          <Box
                            component="details"
                            sx={{
                              mt: 1,
                              borderTop: "1px solid rgba(25,36,47,.07)",
                              "&[open] summary": { mb: .8 },
                            }}
                          >
                            <Box
                              component="summary"
                              sx={{
                                pt: .9,
                                cursor: "pointer",
                                color: "rgba(25,36,47,.62)",
                                fontSize: ".78rem",
                                fontWeight: 900,
                                "&::marker": { color: BRAND.orange },
                              }}
                            >
                              Detalles del proyecto
                            </Box>
                            <Stack spacing={.55} sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)", overflowWrap: "anywhere" }}>
                                Dirección: /{projectItem.slug}
                              </Typography>
                              {projectItem.dbName ? (
                                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)", overflowWrap: "anywhere" }}>
                                  Base: {projectItem.dbName}
                                </Typography>
                              ) : null}
                              {projectItem.domains?.length ? (
                                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)", overflowWrap: "anywhere" }}>
                                  Dominios: {projectItem.domains.join(", ")}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" sx={{ pt: .2 }}>
                                <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                                {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                                  <Chip key={label} size="small" label={label} variant="outlined" />
                                )) : <Chip size="small" label="Sin módulos" />}
                              </Stack>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                              <Chip size="small" label={`Billing: ${projectItem.billing?.status || "trial"}`} color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"} />
                              <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                              {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                                <Chip key={label} size="small" label={label} variant="outlined" />
                              )) : <Chip size="small" label="Sin módulos" />}
                            </Stack>
                          </>
                        )}
                      </Box>
                      {compactProjectCards ? (
                        <Stack spacing={.8} sx={{ width: "100%", maxWidth: { md: 220 }, flexShrink: 0 }}>
                          <Button
                            fullWidth
                            variant={isActive ? "contained" : "outlined"}
                            disabled={!projectUsable}
                            onClick={() => setActiveProject(projectItem)}
                            sx={{
                              minHeight: 42,
                              borderRadius: 2.2,
                              textTransform: "none",
                              fontWeight: 900,
                              bgcolor: isActive ? BRAND.orange : "transparent",
                              borderColor: "rgba(25,36,47,0.18)",
                            }}
                          >
                            {projectUsable ? (isActive ? "Proyecto activo" : "Usar proyecto") : "Acceso suspendido"}
                          </Button>
                          <ProjectActionsMenu
                            project={projectItem}
                            superAdmin={superAdmin}
                            showPlan={Boolean(planAccess?.visible)}
                            showLanding={Boolean(modules.landing && landingAccess?.visible)}
                            showProducts={Boolean(modules.products && productsAccess?.visible)}
                            showOrders={Boolean(modules.orders && ordersAccess?.visible)}
                            onNavigate={navigate}
                          />
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                          <Button variant={isActive ? "contained" : "outlined"} disabled={!projectUsable} onClick={() => setActiveProject(projectItem)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, bgcolor: isActive ? BRAND.orange : "transparent", borderColor: "rgba(25,36,47,0.18)" }}>
                            {projectUsable ? "Usar" : "Acceso suspendido"}
                          </Button>
                          {superAdmin && (
                            <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/general`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Administrar
                            </Button>
                          )}
                          {planAccess?.visible ? (
                            <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/suscripcion`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}>
                              Plan
                            </Button>
                          ) : null}
                          {modules.landing && landingAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pagina`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Landing
                            </Button>
                          ) : null}
                          {modules.products && productsAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/productos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Productos
                            </Button>
                          ) : null}
                          {modules.orders && ordersAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pedidos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Pedidos
                            </Button>
                          ) : null}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
